import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';
import useUI from 'src/hooks/use-ui';

import {
  ProductRecommendationSource,
  ProductRecommendationSection,
} from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';
import { ProductCarousel } from 'src/components/product-carousel';
import { ProductCarousel as ProductCarouselV2 } from 'src/components/carousel-v2/types/product-carousel';
import { useSponsoredProductHandlers } from 'src/utils/ads/hooks/sponsored-products';
import { useCartRecommendationsCarousel } from './use-cart-recommendations-carousel';

type CartRecommendationsCarouselProps = {
  mt?: string;
  alwaysUseSmallCards?: boolean;
};

export function CartRecommendationsCarousel({
  mt,
  alwaysUseSmallCards = false,
}: CartRecommendationsCarouselProps): JSX.Element {
  const { t } = useTranslation();
  const { closeCartDrawer } = useUI();
  const carouselProps = useCartRecommendationsCarousel();
  const { products, loading, placement } = carouselProps;
  const flags = useFlags();
  const isProductCardsV2Enabled = flags['growth.ecomm.product-card-v2.rollout'] ?? false;
  const title = 'Before You Go';

  const { handleSponsoredProductClick, handleSponsoredProductImpression } = useSponsoredProductHandlers({
    trackerSource: ProductRecommendationSource.moloco,
    adPlacement: 'cart.carousel',
  });

  const handleProductClick = (product, index): void => {
    handleSponsoredProductClick(product, index);
    closeCartDrawer();
  };

  if (isProductCardsV2Enabled) {
    return (
      <Wrapper>
        <ProductCarouselV2
          title={title}
          products={products}
          isLoading={loading}
          source={ProductRecommendationSource.crossingMinds}
          isSmallTitle
          mt={mt}
          gradientColor='var(--dcm-cart-bg)'
          onProductClick={handleProductClick}
          onProductView={handleSponsoredProductImpression}
          onQuickAddToCart={handleSponsoredProductClick}
          alwaysUseSmallCards={alwaysUseSmallCards}
          placement={placement}
        />
      </Wrapper>
    );
  }

  return (
    <ProductCarousel
      isMini
      heading={t(`common.beforeYouGo`, title)}
      trackerSource={ProductRecommendationSource.crossingMinds}
      trackerSection={ProductRecommendationSection.cartRecommendations}
      {...carouselProps}
    />
  );
}

const Wrapper = styled.div`
  padding: 0 20px;
`;
